import React, {useContext, useEffect, useRef, useState} from 'react';
import {Button, Form, Input, InputNumber, InputRef, Popconfirm, Space, Table, Typography} from 'antd';
import {observer} from "mobx-react";
import {CardStyled} from "../../../components/styled/card.styled";
import {StoreContext} from "../../../store.context";
import {Translation} from "../../../types/modules";
import type {ColumnType} from 'antd/es/table';
import type {FilterConfirmProps} from 'antd/es/table/interface';
import {SearchOutlined} from '@ant-design/icons';
import Highlighter from 'react-highlight-words';
import {Translate} from "../../../components/util/translation.util";
import {TranslationDomainEnum} from "../../../types/enums";
import {Helmet} from "react-helmet-async";

interface EditableCellProps extends React.HTMLAttributes<HTMLElement> {
    editing: boolean;
    dataIndex: string;
    title: any;
    inputType: 'number' | 'text';
    record: Translation;
    index: number;
    children: React.ReactNode;
}

type DataIndex = keyof Translation;

const EditableCell: React.FC<EditableCellProps> = ({
       editing,
       dataIndex,
       title,
       inputType,
       record,
       index,
       children,
       ...restProps
   }) => {
    const inputNode = inputType === 'number' ? <InputNumber /> : <Input />;

    return (
        <td {...restProps}>
            {editing ? (
                <Form.Item
                    name={dataIndex}
                    style={{ margin: 0 }}
                    rules={[
                        {
                            required: true,
                            message: `Please Input ${title}!`,
                        },
                    ]}
                >
                    {inputNode}
                </Form.Item>
            ) : (
                children
            )}
        </td>
    );
};

export const List: React.FC = observer(() => {
    const [ form] = Form.useForm();
    const [ editingKey, setEditingKey] = useState('');
    const [ translations, setTranslations] = useState<Translation[]>([]);

    const [searchedColumns, setSearchedColumns] = useState<any>([]);
    const searchInput = useRef<InputRef>(null);

    const {
        appStore: {
            moduleStore: {
                translationStore: {
                    list,
                    edit,
                    items,
                    total,
                    current,
                    pageSize,
                    loading,
                    invalidateCache,
                    setTablePaginationData,
                    setTableFilterData,
                }
            },
            translate
        },
    } = useContext(StoreContext);

    const translationDomain = TranslationDomainEnum.MESSAGES;
    const title = translate('userMenuItemEnum.translations', {}, translationDomain);

    const isEditing = (record: Translation) => record._key === editingKey;

    const editMode = (record: Translation) => {
        form.setFieldsValue({ en: record.en, nl: record.nl });
        setEditingKey(record._key);
    };

    const cancel = () => {
        setEditingKey('');
    };

    const onPaginationChange = async (event: any) => {
        setTablePaginationData({
            current: event.current,
            pageSize: event.pageSize,
            field: null,
            order: null
        });

        list();
    }

    useEffect(() => {
        setTranslations(items as Translation[]);
    }, [items]);

    const handleAddSearchedColumn = (field: string, value: string) => {
        setSearchedColumns([...searchedColumns, { field, value }]);
    }

    const handleRemoveSearchedColumn = (dataIndex: DataIndex) => {
        const updatedData = [...searchedColumns];
        updatedData.splice(searchedColumns.findIndex((item: any) => item.field === dataIndex), 1);
        setSearchedColumns(updatedData);
    }

    const handleSearch = (
        selectedKeys: string[],
        confirm: (param?: FilterConfirmProps) => void,
        dataIndex: DataIndex,
    ) => {
        handleAddSearchedColumn(dataIndex, selectedKeys[0]);
    };

    const handleReset = (clearFilters: () => void, dataIndex: DataIndex) => {
        handleRemoveSearchedColumn(dataIndex);
        clearFilters();
    };

    //when table active header filters change or component mounts, lets make an API call
    useEffect(() => {
        setTableFilterData && setTableFilterData(searchedColumns);
        list();
    }, [searchedColumns, list, setTableFilterData])

    const save = async (key: React.Key) => {
        try {
            const row = (await form.validateFields()) as Translation;
            const index = translations.findIndex((item) => key === item._key);
            if (index > -1) {
                const item = translations[index];
                const editedTranslation = { ...item, ...row };
                await edit(editedTranslation)
            }
            setEditingKey('');
        } catch (errInfo) {}
    };

    const getColumnSearchProps = (dataIndex: DataIndex): ColumnType<Translation> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    ><Translate message={`global.button.search`} /></Button>

                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters, dataIndex)}
                        size="small"
                        style={{ width: 90 }}
                    ><Translate message={`global.button.reset`} /></Button>

                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    ><Translate message={`global.button.close`} /></Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
        ),
        onFilter: (value, record) =>
            record[dataIndex]
                .toString()
                .toLowerCase()
                .includes((value as string).toLowerCase()),
        onFilterDropdownOpenChange: (visible) => {
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        },
        render: (text) => {
            let searchedColumnIndex = searchedColumns.findIndex((item: any) => item.field === dataIndex);

            if (searchedColumnIndex < 0) {
                return text;
            }

            return <Highlighter
                highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                searchWords={[searchedColumns[searchedColumnIndex].value]}
                autoEscape
                textToHighlight={text ? text.toString() : ''}
            />
        }
    });

    const columns = [
        {
            title: 'ID',
            dataIndex: '_id',
            width: '5%',
            editable: false,
        },
        {
            title: 'Domain',
            dataIndex: '_domain',
            width: '15%',
            editable: false,
            ...getColumnSearchProps('_domain'),
        },
        {
            title: 'Key',
            dataIndex: '_key',
            width: '15%',
            editable: false,
            ...getColumnSearchProps('_key'),
        },
        {
            title: 'EN',
            dataIndex: 'en',
            width: '25%',
            editable: true,
            ...getColumnSearchProps('en'),
        },
        {
            title: 'NL',
            dataIndex: 'nl',
            width: '25%',
            editable: true,
            ...getColumnSearchProps('nl'),
        },
        {
            title: 'operation',
            dataIndex: 'operation',
            render: (_: any, record: Translation) => {
                const editable = isEditing(record);
                return editable ? (
                    <span>
                        <Typography.Link onClick={() => save(record._key)} style={{ marginRight: 8 }}>
                            <Translate message={`global.button.save`} />
                        </Typography.Link>
                        <Popconfirm title="Sure to cancel?" onConfirm={cancel}>
                            <a href={'/'}><Translate message={`global.button.cancel`} /></a>
                        </Popconfirm>
                    </span>
                ) : (
                    <Typography.Link disabled={editingKey !== ''} onClick={() => editMode(record)}>
                        <Translate message={`global.button.edit`} />
                    </Typography.Link>
                );
            },
        },
    ];

    const mergedColumns = columns.map((col) => {
        //@ts-ignore
        if (!col.editable) {
            return col;
        }
        return {
            ...col,
            onCell: (record: Translation) => ({
                record,
                inputType: 'text',
                dataIndex: col.dataIndex,
                title: col.title,
                editing: isEditing(record),
            }),
        };
    });

    return (
        <React.Fragment>
            <Helmet>
                <title>{`CL BO - ${title}`}</title>
            </Helmet>
            <Space direction="vertical" size="small" style={{ display: 'flex' }}>
                <CardStyled title={<Translate message={`overview.show_grid`} domain={TranslationDomainEnum.LEXIK_TRANSLATION_BUNDLE} />} extra={
                    <Space>
                        <Button type={`default`} onClick={async () => {
                            await invalidateCache();
                        }}><Translate message={`translation.invalidate_cache`} /></Button>
                    </Space>
                }>
                    <Form form={form} component={false}>
                        <Table
                            loading={loading}
                            components={{
                                body: {
                                    cell: EditableCell,
                                },
                            }}
                            bordered
                            dataSource={translations}
                            //@ts-ignore
                            columns={mergedColumns}
                            rowClassName="editable-row"
                            rowKey={`_key`}
                            onChange={onPaginationChange}
                            pagination={{ current, pageSize, total, position: ['bottomCenter'] }}
                        />
                    </Form>
                </CardStyled>
            </Space>
        </React.Fragment>
    )
})